import { Link } from "react-router-dom";
import { Instagram, Facebook, Mail, Phone, MapPin, Leaf } from "lucide-react";
import { useSettings } from "@/context/AppContext";

export default function Footer() {
  const { settings } = useSettings();
  if (!settings) return null;

  return (
    <footer className="bg-forest text-cream relative overflow-hidden" data-testid="site-footer">
      <div className="container-px max-w-7xl mx-auto py-20 grid grid-cols-1 md:grid-cols-12 gap-12">        <div className="md:col-span-5">
          <Link to="/" className="flex items-center gap-2.5">
            {(settings.logo_light || settings.logo) ? (
              <img
                src={settings.logo_light || settings.logo}
                alt={settings.company_name}
                style={{ height: `${Math.max(24, Math.min(140, settings.logo_light_size || 48))}px` }}
                className="w-auto max-w-[280px] object-contain"
              />
            ) : (
              <>
                <span className="w-9 h-9 rounded-full bg-cream/10 flex items-center justify-center">
                  <Leaf className="w-4 h-4" strokeWidth={1.5} />
                </span>
                <span className="font-display text-3xl tracking-tight">{settings.company_name}</span>
              </>
            )}
          </Link>
          <p className="mt-6 text-cream/70 text-sm max-w-md leading-relaxed">
            {settings.company_tagline}. Création, entretien et aménagement d'espaces verts en Suisse romande.
          </p>
          <div className="flex items-center gap-3 mt-8">
            {settings.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-cream hover:text-forest transition"
                data-testid="footer-instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}
            {settings.facebook_url && (
              <a
                href={settings.facebook_url}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 rounded-full border border-cream/20 flex items-center justify-center hover:bg-cream hover:text-forest transition"
                data-testid="footer-facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        <div className="md:col-span-3">
          <p className="label-eyebrow text-cream/60 mb-5">Navigation</p>
          <ul className="space-y-3 text-sm text-cream/80">
            <li><Link to="/" className="hover:text-cream">Accueil</Link></li>
            <li><Link to="/a-propos" className="hover:text-cream">À propos</Link></li>
            <li><Link to="/services" className="hover:text-cream">Services</Link></li>
            <li><Link to="/realisations" className="hover:text-cream">Réalisations</Link></li>
            <li><Link to="/contact" className="hover:text-cream">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="label-eyebrow text-cream/60 mb-5">Contact</p>
          <ul className="space-y-4 text-sm text-cream/80">
            {settings.contact_phone && (
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 shrink-0" />
                <a href={`tel:${settings.contact_phone.replace(/\s/g, "")}`} className="hover:text-cream">
                  {settings.contact_phone}
                </a>
              </li>
            )}
            {settings.contact_email && (
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 shrink-0" />
                <a href={`mailto:${settings.contact_email}`} className="hover:text-cream">
                  {settings.contact_email}
                </a>
              </li>
            )}
            {settings.contact_address && (
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>{settings.contact_address}</span>
              </li>
            )}
            {settings.contact_hours && (
              <li className="text-cream/60 text-xs whitespace-pre-line pt-2 border-t border-cream/10 mt-4">
                {settings.contact_hours}
              </li>
            )}
          </ul>
        </div>
      </div>
      {settings.partners?.length > 0 && (
        <div className="border-t border-cream/10">
          <div className="container-px max-w-7xl mx-auto py-14">
            <p className="label-eyebrow text-cream/60 mb-10 text-center">Nos partenaires</p>
            <div className="flex flex-wrap items-center justify-center gap-x-12 sm:gap-x-16 lg:gap-x-20 gap-y-10" data-testid="footer-partners">
              {settings.partners.map((p) => {
                const sz = Math.max(40, Math.min(180, settings.partners_logo_size || 80));
                const inner = p.logo ? (
                  <img
                    src={p.logo}
                    alt={p.name}
                    style={{ height: `${sz}px`, maxWidth: `${Math.round(sz * 2.2)}px` }}
                    className="w-auto object-contain opacity-80 hover:opacity-100 transition duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <span className="text-cream/80 hover:text-cream text-base sm:text-lg font-medium tracking-wide">{p.name}</span>
                );
                return p.url ? (
                  <a
                    key={p.id}
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    title={p.name}
                    data-testid={`partner-link-${p.id}`}
                    className="flex items-center"
                  >
                    {inner}
                  </a>
                ) : (
                  <div key={p.id} className="flex items-center" title={p.name}>{inner}</div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className="border-t border-cream/10">
        <div className="container-px max-w-7xl mx-auto py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-cream/50">
          <p>© {new Date().getFullYear()} {settings.company_name}. Tous droits réservés.</p>
          <div className="flex items-center gap-5">
            <Link to="/mentions-legales" className="hover:text-cream/80">Mentions légales</Link>
            <Link to="/confidentialite" className="hover:text-cream/80">Confidentialité</Link>
            <Link to="/admin/login" className="hover:text-cream/80 opacity-50" data-testid="footer-admin-link">·</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
